import React from "react"
import { Modal, Form, Input, Button, Alert, Typography, Space, Divider } from "antd"
import { MailOutlined, LockOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { verifyGuestEmail } from "store/guest-access"
import { routes } from "router"

const { Title, Text } = Typography

/**
 * Shown to logged-out users who arrive at a booking details page via an email link.
 * Requires them to enter the primary guest email to confirm identity before access is granted.
 */
const GuestEmailVerifyModal = ({ bookingUuid }) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error } = useSelector((state) => state.guestAccess)

  const handleSubmit = async (values) => {
    try {
      await dispatch(verifyGuestEmail(bookingUuid, values.email))
      // Success: Redux state updates authorized=true, component unmounts via parent re-render
    } catch {
      // Error is stored in Redux state and displayed below
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f5f5"
      }}
    >
      <Modal
        open={true}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={440}
        styles={{ mask: { background: "transparent" } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#e6f4ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}
            >
              <LockOutlined style={{ fontSize: 24, color: "#1677ff" }} />
            </div>
            <Title level={4} style={{ marginBottom: 4 }}>
              Verify Your Identity
            </Title>
            <Text type="secondary">
              Enter the email address associated with this booking to view your trip details.
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              icon={<MailOutlined />}
            />
          )}

          <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: "Please enter your email address" },
                { type: "email", message: "Please enter a valid email address" }
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                placeholder="your@email.com"
                size="large"
                autoComplete="email"
                autoFocus
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
              >
                Access Trip Details
              </Button>
            </Form.Item>
          </Form>

          <Divider plain style={{ margin: "0" }}>
            <Text type="secondary" style={{ fontSize: 13 }}>or</Text>
          </Divider>

          <Button
            size="large"
            block
            onClick={() => navigate(routes.signInV2())}
          >
            Sign In
          </Button>
        </Space>
      </Modal>
    </div>
  )
}

export default GuestEmailVerifyModal
